import {
  FaroButton,
  FaroText,
  grey,
  InfoIcon,
  useBreakpointMdUp,
} from "@faro-lotv/flat-ui";
import { Stack } from "@mui/material";
import type { JSX } from "react";
import { WizardNavigation } from "./use-wizard-navigation";

type WizardFooterProps = Pick<
  WizardNavigation,
  | "activeStep"
  | "activeIndex"
  | "canContinue"
  | "isBusy"
  | "isLastStep"
  | "next"
  | "back"
>;

/**
 * @param props The navigation state and actions of the wizard
 * @returns the actions to move through a wizard, with the reason why the user can't continue yet
 */
export function WizardFooter(props: WizardFooterProps): JSX.Element {
  const { canContinue } = props;
  return (
    <Stack sx={{ gap: 1, pt: 1.5 }}>
      <WizardBlockedReason canContinue={canContinue} />
      <WizardActions {...props} />
    </Stack>
  );
}

/** @returns why the user can't continue yet, nothing if they can */
export function WizardBlockedReason({
  canContinue,
}: Pick<WizardNavigation, "canContinue">): JSX.Element | null {
  if (canContinue === true) return null;
  return (
    <Stack direction="row" sx={{ gap: 1, alignItems: "flex-start" }}>
      <InfoIcon sx={{ color: grey[600], width: 18, height: 18, mt: 0.25 }} />
      <FaroText variant="bodyS" color={grey[700]} role="status">
        {canContinue}
      </FaroText>
    </Stack>
  );
}

/** @returns the back and primary buttons of a wizard */
export function WizardActions({
  activeStep,
  activeIndex,
  canContinue,
  isBusy,
  isLastStep,
  next,
  back,
}: WizardFooterProps): JSX.Element {
  const isMdUp = useBreakpointMdUp();
  const size = isMdUp ? "m" : "l";
  const primaryLabel =
    activeStep.primaryActionLabel ?? (isLastStep ? "Finish" : "Next");

  return (
    // Back stays on the far left and the primary action on the far right, whatever the step
    <Stack direction="row" sx={{ gap: 1, justifyContent: "space-between" }}>
      {activeIndex > 0 && !activeStep.preventBack ? (
        <FaroButton
          variant="ghost"
          size={size}
          onClick={back}
          disabled={isBusy}
          sx={{ flexGrow: isMdUp ? 0 : 1 }}
        >
          Back
        </FaroButton>
      ) : (
        <span />
      )}
      <FaroButton
        size={isLastStep ? "m" : size}
        onClick={next}
        disabled={canContinue !== true}
        isLoading={isBusy}
        sx={{ flexGrow: isMdUp ? 0 : 2 }}
      >
        {primaryLabel}
      </FaroButton>
    </Stack>
  );
}
